import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  Divider,
  CircularProgress,
  Popover,
  Paper,
  Grid
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import ColorLensIcon from '@mui/icons-material/ColorLens';
import config from '../config';

// Predefined colors for a simple color picker
const colorOptions = [
  '#f44336', // Red
  '#e91e63', // Pink
  '#9c27b0', // Purple
  '#673ab7', // Deep Purple
  '#3f51b5', // Indigo
  '#2196f3', // Blue
  '#03a9f4', // Light Blue
  '#00bcd4', // Cyan
  '#009688', // Teal
  '#4caf50', // Green
  '#8bc34a', // Light Green
  '#cddc39', // Lime
  '#ffeb3b', // Yellow
  '#ffc107', // Amber
  '#ff9800', // Orange
  '#ff5722', // Deep Orange
  '#795548', // Brown
  '#607d8b', // Blue Grey
  '#9e9e9e', // Grey
  '#000000'  // Black
];

function Settings() {
  const { token, user, updateUser } = useAuth();
  const [userData, setUserData] = useState({
    username: '',
    email: '',
    color: '#2196f3' // Default color
  });
  const [passwords, setPasswords] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [message, setMessage] = useState({ type: '', text: '' });
  const [loading, setLoading] = useState(false);
  const [colorPickerAnchor, setColorPickerAnchor] = useState(null);
  const [customColor, setCustomColor] = useState('');

  useEffect(() => {
    // Fetch user data
    setLoading(true);
    fetch(`${config.apiUrl}/user/profile`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })
      .then(res => res.json())
      .then(data => {
        setUserData({
          username: data.username,
          email: data.email,
          color: data.color || '#2196f3' // Use default if not set
        });
        setCustomColor(data.color || '#2196f3');
        setLoading(false);
      })
      .catch(err => {
        setMessage({ type: 'error', text: 'Failed to load user data' });
        setLoading(false);
      });
  }, [token]);

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const response = await fetch(`${config.apiUrl}/user/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(userData),
      });

      const data = await response.json();

      if (!response.ok) throw new Error(data.error);
      
      // Update the user context if available
      if (updateUser) {
        updateUser({
          ...user,
          ...userData
        });
      }
      
      setMessage({ type: 'success', text: 'Profile updated successfully' });
      setLoading(false);
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (passwords.newPassword !== passwords.confirmPassword) {
      setMessage({ type: 'error', text: 'New passwords do not match' });
      return;
    }
    
    try {
      setLoading(true);
      const response = await fetch(`${config.apiUrl}/user/password`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          currentPassword: passwords.currentPassword,
          newPassword: passwords.newPassword
        }),
      });

      const data = await response.json();

      if (!response.ok) throw new Error(data.error);
      
      setPasswords({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
      
      setMessage({ type: 'success', text: 'Password changed successfully' });
      setLoading(false);
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
      setLoading(false);
    }
  };

  const handleColorChange = (color) => {
    setUserData({
      ...userData,
      color
    });
    setCustomColor(color);
  };

  const openColorPicker = (event) => {
    setColorPickerAnchor(event.currentTarget);
  };

  const closeColorPicker = () => {
    setColorPickerAnchor(null);
  };

  const handleCustomColorChange = (e) => {
    const color = e.target.value;
    setCustomColor(color);
    
    // Validate if it's a proper hex color
    if (/^#([0-9A-F]{3}){1,2}$/i.test(color)) {
      handleColorChange(color);
    }
  };

  const colorPickerOpen = Boolean(colorPickerAnchor);

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto', mt: 4 }}>
      <Typography variant="h4" gutterBottom>
        Settings
      </Typography>
      
      {message.text && (
        <Alert severity={message.type} sx={{ mb: 2 }}>
          {message.text}
        </Alert>
      )}
      
      {loading && <CircularProgress sx={{ display: 'block', mx: 'auto', my: 2 }} />}
      
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Profile Settings
          </Typography>
          <form onSubmit={handleProfileUpdate}>
            <TextField
              label="Username"
              fullWidth
              margin="normal"
              value={userData.username}
              onChange={(e) => setUserData({ ...userData, username: e.target.value })}
            />
            <TextField
              label="Email"
              type="email"
              fullWidth
              margin="normal"
              value={userData.email}
              onChange={(e) => setUserData({ ...userData, email: e.target.value })}
            />
            
            <Box sx={{ mt: 2, mb: 1 }}>
              <Typography variant="subtitle1" gutterBottom>
                Calendar Color
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box 
                  onClick={openColorPicker}
                  sx={{ 
                    width: 40, 
                    height: 40, 
                    borderRadius: '50%', 
                    bgcolor: userData.color,
                    cursor: 'pointer',
                    border: '2px solid #ccc',
                    mr: 2,
                    transition: 'transform 0.2s',
                    '&:hover': {
                      transform: 'scale(1.1)',
                    }
                  }} 
                />
                <Button 
                  variant="outlined" 
                  startIcon={<ColorLensIcon />}
                  onClick={openColorPicker}
                >
                  Change Color
                </Button>
              </Box>
            </Box>
            
            <Popover
              open={colorPickerOpen}
              anchorEl={colorPickerAnchor}
              onClose={closeColorPicker}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
            >
              <Paper sx={{ p: 2, width: 280 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Select a color:
                </Typography>
                
                <Grid container spacing={1} sx={{ mb: 2 }}>
                  {colorOptions.map((color) => (
                    <Grid item key={color}>
                      <Box 
                        onClick={() => handleColorChange(color)}
                        sx={{ 
                          width: 30, 
                          height: 30, 
                          bgcolor: color,
                          cursor: 'pointer',
                          border: userData.color === color ? '2px solid black' : '1px solid #ccc',
                          borderRadius: '4px',
                          transition: 'transform 0.1s',
                          '&:hover': {
                            transform: 'scale(1.1)',
                          }
                        }} 
                      />
                    </Grid>
                  ))}
                </Grid>
                
                <Typography variant="subtitle2" gutterBottom>
                  Or enter a custom color:
                </Typography>
                
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <TextField
                    size="small"
                    value={customColor}
                    onChange={handleCustomColorChange}
                    placeholder="#RRGGBB"
                    fullWidth
                    sx={{ mr: 1 }}
                  />
                  <Box 
                    sx={{ 
                      width: 30, 
                      height: 30, 
                      bgcolor: customColor,
                      border: '1px solid #ccc',
                      borderRadius: '4px'
                    }} 
                  />
                </Box>
                
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button 
                    size="small" 
                    variant="contained" 
                    onClick={closeColorPicker}
                  >
                    Done
                  </Button>
                </Box>
              </Paper>
            </Popover>
            
            <Button
              type="submit"
              variant="contained"
              sx={{ mt: 3 }}
              disabled={loading}
            >
              Update Profile
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Change Password
          </Typography>
          <form onSubmit={handlePasswordChange}>
            <TextField
              label="Current Password"
              type="password"
              fullWidth
              margin="normal"
              value={passwords.currentPassword}
              onChange={(e) => setPasswords({ ...passwords, currentPassword: e.target.value })}
            />
            <TextField
              label="New Password"
              type="password"
              fullWidth
              margin="normal"
              value={passwords.newPassword}
              onChange={(e) => setPasswords({ ...passwords, newPassword: e.target.value })}
            />
            <TextField
              label="Confirm New Password"
              type="password"
              fullWidth
              margin="normal"
              value={passwords.confirmPassword}
              onChange={(e) => setPasswords({ ...passwords, confirmPassword: e.target.value })}
            />
            <Button
              type="submit"
              variant="contained"
              sx={{ mt: 2 }}
              disabled={loading}
            >
              Change Password
            </Button>
          </form>
        </CardContent>
      </Card>

      {user?.isAdmin && (
        <Box sx={{ mt: 3, mb: 2 }}>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            Admin Options
          </Typography>
          <Button
            variant="contained"
            color="primary"
            component={Link}
            to="/admin"
            startIcon={<AdminPanelSettingsIcon />}
            sx={{ mt: 1 }}
          >
            Go to Admin Panel
          </Button>
        </Box>
      )}
    </Box>
  );
}

export default Settings; 